import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import cn from 'classnames';
import { ButtonGroup, Button, Icon } from 'ui';
import Modal from 'components/shared/modal';
import RadioFieldGroup from 'components/shared/RadioFieldGroup/index';
import RadioField from 'components/shared/RadioFieldGroup/RadioField';
import Select from 'components/shared/select';
import DateInputButton from 'components/shared/DateInputButton';
import InfoTooltip from 'components/shared/InfoTooltip';
import { getComponentClassHelper } from 'ui/helpers';
import { hasFeatureFlags } from 'utils/features';

export const componentClass = getComponentClassHelper('ExpirationDateModal');

const ExpirationTypeRadio = ({ value, ...props }) => {
  const hasCertificationRenewalsFeature = hasFeatureFlags([
    'certifications_renewal',
  ]);

  return (
    <RadioField
      name="certificateExpirationType"
      className={cn(`${componentClass('radio')}`, {
        'd-n': value === 'recurring' && !hasCertificationRenewalsFeature,
      })}
      value={value}
      {...props}
    >
      <div className={componentClass('radio-label')}>
        {value === 'never' && t('certifications.expiration_types.never')}
        {value === 'once' && t('certifications.expiration_types.once')}
        {value === 'recurring' &&
          t('certifications.expiration_types.recurring')}
        <div className={componentClass('radio-description')}>
          {value === 'never' &&
            t('certifications.expiration_modal.never_description')}
          {value === 'once' &&
            t('certifications.expiration_modal.once_description')}
          {value === 'recurring' &&
            t('certifications.expiration_modal.recurring_description')}
        </div>
      </div>
    </RadioField>
  );
};

const ExpirationDateModal = ({
  visible,
  closeModal,
  updateCertification,
  certification: {
    expiresOn,
    expiresAfterAmount,
    expiresAfterUnit,
    renewalDueAmount,
    renewalDueUnit,
    renews,
    repeatsAmount,
    repeatsUnit,
    recurEndsOnDate,
    recurEndsAfter,
  },
}) => {
  const hasCertificationRenewalsFeature = hasFeatureFlags([
    'certifications_renewal',
  ]);
  const getExpirationType = () => {
    let expirationType;
    if (hasCertificationRenewalsFeature && renews) {
      expirationType = 'recurring';
    } else if (expiresOn || expiresAfterAmount || expiresAfterUnit) {
      expirationType = 'once';
    } else {
      expirationType = 'never';
    }
    return expirationType;
  };
  const [loading, setLoading] = useState(false);
  const [expirationType, setExpirationType] = useState(getExpirationType());
  const [expirationAmount, setExpirationAmount] = useState(
    expiresAfterAmount || '',
  );
  const [expirationUnit, setExpirationUnit] = useState(
    expiresAfterUnit || undefined,
  );
  const [expirationMethod, setExpirationMethod] = useState(
    expirationAmount || expirationUnit ? 'after' : 'on',
  );
  const [expirationDate, setExpirationDate] = useState(expiresOn || null);
  const [renewalAmount, setRenewalAmount] = useState(
    (hasCertificationRenewalsFeature && renewalDueAmount) || '',
  );
  const [renewalUnit, setRenewalUnit] = useState(
    (hasCertificationRenewalsFeature && renewalDueUnit) || undefined,
  );
  const [reoccuringExpirationType, setReoccuringExpirationType] = useState(
    expiresOn !== null ? 'on' : 'every',
  );
  const [reoccuringEndExpirationType, setReoccuringEndExpirationType] =
    useState(recurEndsOnDate !== null ? 'on' : 'after');
  const [repeatAmount, setRepeatAmount] = useState(repeatsAmount || '');
  const [repeatUnit, setRepeatUnit] = useState(repeatsUnit || undefined);
  const [reoccurEndDate, setReoccurEndDate] = useState(recurEndsOnDate || null);
  const [reoccurEndAfter, setReoccurEndAfter] = useState(
    recurEndsAfter || undefined,
  );

  const onDialogClose = () => {
    if (visible) {
      closeModal();
      setExpirationType(getExpirationType());
      setExpirationAmount(expiresAfterAmount || '');
      setExpirationUnit(expiresAfterUnit || undefined);
      setRenewalAmount(renewalDueAmount || '');
      setRenewalUnit(renewalDueUnit || undefined);
      setExpirationMethod(
        expiresAfterAmount || expiresAfterUnit ? 'after' : 'on',
      );
      setExpirationDate(expiresOn || null);
    }
  };

  const getUpdateData = () => {
    if (expirationType === 'never') {
      return {
        expiresOn: null,
        expiresAfterAmount: null,
        expiresAfterUnit: null,
        renewalDueAmount: null,
        renewalDueUnit: null,
        renews: false,
        recurEndsOnDate: null,
      };
    }

    if (expirationType === 'recurring') {
      const expirationDateToSend =
        reoccuringExpirationType === 'every' ? null : expirationDate;

      const recurEndsOnDateToSend =
        reoccuringExpirationType === 'every' ? null : reoccurEndDate;

      const recurEndsAfterValue =
        reoccuringExpirationType === 'on' ? null : reoccurEndAfter;

      // "Expires after" values only apply to "expires every" recurrence.
      const expiresAfterAmountToSend =
        reoccuringExpirationType === 'every' ? Number(expirationAmount) : null;
      const expiresAfterUnitToSend =
        reoccuringExpirationType === 'every' ? Number(expirationUnit) : null;

      return {
        expiresOn: expirationDateToSend,
        expiresAfterAmount: expiresAfterAmountToSend,
        expiresAfterUnit: expiresAfterUnitToSend,
        renewalDueAmount: renewalAmount,
        renewalDueUnit: renewalUnit,
        renews: true,
        repeatsAmount: repeatAmount,
        repeatsUnit: repeatUnit,
        recurEndsOnDate: recurEndsOnDateToSend,
        recurEndsAfter: recurEndsAfterValue,
      };
    }

    if (expirationMethod === 'on') {
      return {
        expiresOn: expirationDate,
        expiresAfterAmount: null,
        expiresAfterUnit: null,
        renewalDueAmount: null,
        renewalDueUnit: null,
        renews: false,
      };
    }

    return {
      expiresOn: null,
      expiresAfterAmount: Number(expirationAmount),
      expiresAfterUnit: Number(expirationUnit),
      renewalDueAmount: null,
      renewalDueUnit: null,
      renews: false,
    };
  };

  const hasCertificationInDevelopmentFeature = hasFeatureFlags([
    'certifications_in_development',
  ]);

  const validateRecurringOptions = () => {
    if (hasCertificationInDevelopmentFeature) {
      if (reoccuringExpirationType === 'on') {
        if (expirationDate && repeatAmount && repeatAmount > 0 && repeatUnit) {
          return true;
        }
        return false;
      }

      if (reoccuringExpirationType === 'every') {
        if (expirationAmount && expirationAmount > 0 && expirationUnit) {
          return true;
        }
      }
    }

    if (!hasCertificationInDevelopmentFeature) {
      if (expirationAmount && expirationAmount > 0 && expirationUnit) {
        if (
          (renewalAmount && renewalAmount > 0 && renewalUnit) ||
          (!renewalAmount && !renewalUnit)
        ) {
          return true;
        }
        return false;
      }
    }

    return false;
  };

  const validateUpdateData = () => {
    if (expirationType === 'never') {
      return true;
    }
    if (expirationType === 'recurring') {
      return validateRecurringOptions();
    }
    if (expirationType === 'once') {
      if (expirationMethod === 'on' && expirationDate) {
        return true;
      }
      if (
        expirationMethod === 'after' &&
        expirationAmount &&
        expirationAmount > 0 &&
        expirationUnit
      ) {
        return true;
      }
      return false;
    }
    return false;
  };

  const resetExpiresAfter = () => {
    setExpirationAmount('');
    setExpirationUnit(undefined);
  };

  const resetRenewalDue = () => {
    setRenewalAmount('');
    setRenewalUnit(undefined);
  };

  const resetState = () => {
    if (expirationType === 'never') {
      resetExpiresAfter();
      resetRenewalDue();
      setExpirationDate(null);
      setExpirationMethod('on');
      setReoccurEndDate(null);
    } else if (expirationType === 'recurring') {
      setExpirationMethod('after');
    } else if (expirationMethod === 'on') {
      resetExpiresAfter();
      resetRenewalDue();
      setReoccurEndDate(null);
    } else {
      setExpirationDate(null);
      resetRenewalDue();
    }
  };

  const setCertificationExpiration = () => {
    setLoading(true);
    updateCertification(getUpdateData()).then(() => {
      resetState();
      setLoading(false);
      closeModal();
    });
  };

  return (
    <Modal
      containerClassName={componentClass()}
      show={visible}
      dismissCallback={onDialogClose}
      canClickAway={false}
      onDismiss={onDialogClose}
      enableFloatingDismiss
    >
      <div className={componentClass('header')}>
        <h2>{t('certifications.expiration')}</h2>
        <div className={componentClass('header-description')}>
          {t('certifications.expiration_modal.description')}
        </div>
      </div>
      <div className={componentClass('section-label')}>
        {t('certifications.expiration_modal.expiration_type')}
      </div>

      <RadioFieldGroup
        className={componentClass('checkbox')}
        name="certificateExpirationType"
        selectedValue={expirationType}
        onChange={e => setExpirationType(e.target.value)}
      >
        <ExpirationTypeRadio value="never" />
        <ExpirationTypeRadio value="once" />
        <ExpirationTypeRadio value="recurring" />
      </RadioFieldGroup>

      {expirationType === 'once' && (
        <>
          <div className={componentClass('section-label')}>
            {t('certifications.expiration_modal.expiration_settings')}
          </div>

          <div className={componentClass('expiration-settings')}>
            <div className={componentClass('expires-label')}>
              {t('certifications.expiration_modal.expires')}
            </div>

            <Select
              className="mr-10"
              name="expires_on"
              id="expires_on"
              value={expirationMethod}
              onChange={e => {
                setExpirationMethod(e.target.value);
              }}
            >
              <option value="on">
                {t('certifications.expiration_modal.expires_on')}
              </option>
              <option value="after">
                {t('certifications.expiration_modal.expires_after')}
              </option>
            </Select>

            {expirationMethod === 'on' ? (
              <div className="AssignModal-dateField">
                <DatePicker
                  selected={expirationDate && moment(expirationDate)}
                  dateFormat="MM/DD/YYYY"
                  onChange={date =>
                    setExpirationDate(date ? date.toDate() : null)
                  }
                  minDate={moment(Date.now()).add(1, 'days')}
                  customInput={<DateInputButton />}
                />
                <Icon name="calendar" className="expirationIcon" />
              </div>
            ) : (
              <>
                <input
                  className="NewInput NewInput--white AssignModal-recurringAmount w-65 mr-10"
                  type="number"
                  min="1"
                  step="1"
                  value={expirationAmount}
                  onChange={e => setExpirationAmount(e.target.value)}
                />

                <Select
                  onChange={e => setExpirationUnit(e.target.value)}
                  value={expirationUnit}
                  className="AssignModal-recurringUnit mr-10"
                >
                  <option value="">–</option>
                  <option value="1">
                    {t('certifications.expiration_units.days')}
                  </option>
                  <option value="2">
                    {t('certifications.expiration_units.weeks')}
                  </option>
                  <option value="3">
                    {t('certifications.expiration_units.months')}
                  </option>
                  <option value="4">
                    {t('certifications.expiration_units.years')}
                  </option>
                </Select>
              </>
            )}

            <InfoTooltip
              tooltip={t('certifications.expiration_modal.settings_tooltip')}
              className={cn({ 'd-n': expirationMethod === 'on' })}
              noMarginRight
            />
          </div>
        </>
      )}
      {hasCertificationRenewalsFeature && expirationType === 'recurring' && (
        <>
          <div className={componentClass('section-label')}>
            {t('certifications.expiration_modal.expiration_settings')}
          </div>
          <div className="pb-05r llui-text-instrux-small-semibold">
            {t('certifications.expiration_modal.expiration_date')}
          </div>
          <div className={componentClass('expiration-settings')}>
            {!hasCertificationInDevelopmentFeature && (
              <>
                <div className={componentClass('expires-label c-midnight-60')}>
                  {t('certifications.expiration_modal.expires_every')}{' '}
                </div>
                <input
                  className="NewInput NewInput--white AssignModal-recurringAmount w-65"
                  type="number"
                  min="1"
                  step="1"
                  value={expirationAmount}
                  onChange={e => setExpirationAmount(e.target.value)}
                />
                <Select
                  onChange={e => setExpirationUnit(e.target.value)}
                  value={expirationUnit}
                  className="AssignModal-recurringUnit"
                >
                  <option value="">–</option>
                  <option value="1">
                    {t('certifications.expiration_units.days')}
                  </option>
                  <option value="2">
                    {t('certifications.expiration_units.weeks')}
                  </option>
                  <option value="3">
                    {t('certifications.expiration_units.months')}
                  </option>
                  <option value="4">
                    {t('certifications.expiration_units.years')}
                  </option>
                </Select>
                <InfoTooltip
                  tooltip={t(
                    'certifications.expiration_modal.settings_tooltip',
                  )}
                  className="ml-8"
                />
              </>
            )}
            {hasCertificationInDevelopmentFeature && (
              <>
                <div
                  className={componentClass(
                    `expires-label c-midnight-60 pr-8 ${
                      reoccuringExpirationType !== 'every' ? 'w-110' : ''
                    }`,
                  )}
                >
                  {t('certifications.expiration_modal.expires')}
                </div>
                <Select
                  className="AssignModal-recurringUnit mr-10"
                  onChange={e => setReoccuringExpirationType(e.target.value)}
                  value={reoccuringExpirationType}
                  id="exp-interval"
                  ariaLabel="Expiration Interval"
                >
                  <option value="every">every</option>
                  <option value="on">on</option>
                </Select>

                {reoccuringExpirationType === 'on' && (
                  <div className="AssignModal-dateField">
                    <DatePicker
                      selected={expirationDate && moment(expirationDate)}
                      dateFormat="MM/DD/YYYY"
                      onChange={date =>
                        setExpirationDate(date ? date.toDate() : null)
                      }
                      minDate={moment(Date.now()).add(1, 'days')}
                      customInput={<DateInputButton />}
                    />
                    <Icon name="calendar" className="expirationIcon" />
                  </div>
                )}

                {reoccuringExpirationType === 'every' && (
                  <>
                    <input
                      className="NewInput NewInput--white AssignModal-recurringAmount w-65"
                      type="number"
                      min="1"
                      step="1"
                      value={expirationAmount}
                      onChange={e =>
                        setExpirationAmount(parseInt(e.target.value, 10))
                      }
                    />

                    <Select
                      onChange={e =>
                        setExpirationUnit(parseInt(e.target.value, 10))
                      }
                      value={expirationUnit}
                      className="AssignModal-recurringUnit mr-10"
                    >
                      <option value="">–</option>
                      <option value="1">
                        {t('certifications.expiration_units.days')}
                      </option>
                      <option value="2">
                        {t('certifications.expiration_units.weeks')}
                      </option>
                      <option value="3">
                        {t('certifications.expiration_units.months')}
                      </option>
                      <option value="4">
                        {t('certifications.expiration_units.years')}
                      </option>
                    </Select>
                  </>
                )}
              </>
            )}
          </div>
          {hasCertificationInDevelopmentFeature && (
            <>
              {reoccuringExpirationType === 'on' && (
                <div className={componentClass('expiration-settings')}>
                  <div
                    className={componentClass(
                      'expires-label c-midnight-60 w-110',
                    )}
                  >
                    {t('certifications.expiration_modal.repeats_every')}
                  </div>

                  <input
                    className="NewInput NewInput--white AssignModal-recurringAmount w-65"
                    type="number"
                    min="1"
                    step="1"
                    value={repeatAmount}
                    onChange={e =>
                      setRepeatAmount(parseInt(e.target.value, 10))
                    }
                  />

                  <Select
                    onChange={e => setRepeatUnit(parseInt(e.target.value, 10))}
                    value={repeatUnit}
                    className="AssignModal-recurringUnit mr-10"
                  >
                    <option value="">–</option>
                    <option value="1">
                      {t('certifications.expiration_units.days')}
                    </option>
                    <option value="2">
                      {t('certifications.expiration_units.weeks')}
                    </option>
                    <option value="3">
                      {t('certifications.expiration_units.months')}
                    </option>
                    <option value="4">
                      {t('certifications.expiration_units.years')}
                    </option>
                  </Select>
                </div>
              )}

              <div className={componentClass('expiration-settings')}>
                <div
                  className={componentClass(
                    `expires-end-label c-midnight-60 ${
                      reoccuringExpirationType === 'on' ? 'w-110' : 'w-70'
                    }`,
                  )}
                >
                  {t('certifications.expiration_modal.ends')}
                </div>

                <Select
                  className="AssignModal-recurringUnit mr-10"
                  onChange={e => setReoccuringEndExpirationType(e.target.value)}
                  value={reoccuringEndExpirationType}
                  id="exp-end-interval"
                  ariaLabel="Expiration End Interval"
                >
                  <option value="-">-</option>
                  <option value="on">on</option>
                  <option value="after">after</option>
                </Select>
                {reoccuringEndExpirationType === 'on' && (
                  <div className="AssignModal-dateField">
                    <DatePicker
                      selected={reoccurEndDate && moment(reoccurEndDate)}
                      dateFormat="MM/DD/YYYY"
                      onChange={date =>
                        setReoccurEndDate(date ? date.toDate() : null)
                      }
                      minDate={moment(Date.now()).add(1, 'days')}
                      customInput={<DateInputButton />}
                    />
                    <Icon name="calendar" className="expirationIcon" />
                  </div>
                )}
                {reoccuringEndExpirationType === 'after' && (
                  <>
                    <input
                      className="NewInput NewInput--white AssignModal-recurringAmount w-65"
                      type="number"
                      min="1"
                      step="1"
                      value={reoccurEndAfter}
                      onChange={e =>
                        setReoccurEndAfter(parseInt(e.target.value, 10))
                      }
                    />
                    <span>
                      {t('certifications.expiration_modal.occurrences')}
                    </span>
                  </>
                )}
              </div>

              <div className="pb-4 llui-text-instrux-small-semibold">
                {t('certifications.renewals.renewal_assignments')}
              </div>
              <div className="c-midnight-60">
                <p>
                  {t('certifications.renewals.reassignment')}{' '}
                  <b>{t('certifications.renewals.reassignment_schedule')}</b>{' '}
                  {t('certifications.renewals.reassignment_suffix')}
                </p>
              </div>

              <div className={componentClass('expiration-settings')}>
                <div className="c-midnight-60 mr-10">
                  {t('certifications.renewals.due')}
                </div>

                <input
                  className="NewInput NewInput--white AssignModal-recurringAmount w-65"
                  type="number"
                  min="1"
                  step="1"
                  value={renewalAmount}
                  onChange={e => setRenewalAmount(e.target.value)}
                />

                <Select
                  onChange={e => setRenewalUnit(e.target.value)}
                  value={renewalUnit}
                  className="AssignModal-recurringUnit mr-10"
                >
                  <option value="">–</option>
                  <option value="1">
                    {t('certifications.expiration_units.days')}
                  </option>
                  <option value="2">
                    {t('certifications.expiration_units.weeks')}
                  </option>
                  <option value="3">
                    {t('certifications.expiration_units.months')}
                  </option>
                  <option value="4">
                    {t('certifications.expiration_units.years')}
                  </option>
                </Select>

                <div className="c-midnight-60">
                  {t('certifications.renewals.after_assigned')}
                </div>
              </div>
            </>
          )}
        </>
      )}
      <ButtonGroup alignEnd>
        <Button onClick={onDialogClose}>
          {t('certifications.expiration_modal.cancel')}
        </Button>
        <Button
          loading={loading}
          disabled={!validateUpdateData()}
          primary
          onClick={setCertificationExpiration}
        >
          {t('certifications.expiration_modal.apply')}
        </Button>
      </ButtonGroup>
    </Modal>
  );
};

export default ExpirationDateModal;
